import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IInitialPriceEstimate,
  IInitialPriceEstimateAvailabilityResponse,
  IInitialPriceEstimateListResponse,
  IInitialPriceEstimateSingleResponse,
  PENDING_ESTIMATE_STATUSES,
} from "../models/InitialPriceEstimate";
import apiConfig from "../utils/apiConfig";

/**
 * Initial Price Estimate for a lead or quote: whether the customer has it, the lead's runs (newest
 * first, polled while one is in progress) and a manual re-run.
 */
export default function useInitialPriceEstimate(
  leadId: string | undefined,
  componentName = "useInitialPriceEstimate",
) {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  // The server answers false unless the feature is enabled, Admin has active categories and the
  // user can see prices, so the card can hide on a feature that is merely present but disabled.
  const availability = useQuery({
    queryKey: ["initial-price-estimate", "enabled"],
    queryFn: () => get<IInitialPriceEstimateAvailabilityResponse>(`/initial-price-estimate/enabled`),
    staleTime: 1000 * 60 * 10,
  });

  const enabled = availability.data?.data?.enabled === true;

  const estimates = useQuery({
    queryKey: ["initial-price-estimate", leadId],
    queryFn: () => get<IInitialPriceEstimateListResponse>(`/initial-price-estimate/lead/${leadId}`),
    enabled: !!leadId && enabled,
    refetchInterval: (data) => {
      const latest = data?.data?.[0];
      return latest && PENDING_ESTIMATE_STATUSES.includes(latest.status) ? 4000 : false;
    },
  });

  const latest: IInitialPriceEstimate | undefined = estimates.data?.data?.[0];
  const isPending = !!latest && PENDING_ESTIMATE_STATUSES.includes(latest.status);

  const run = useMutation(
    () => {
      if (!leadId) throw new Error("Missing leadId");
      return post<object, IInitialPriceEstimateSingleResponse>(`/initial-price-estimate/lead/${leadId}/run`, {});
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["initial-price-estimate", leadId]);
      },
    },
  );

  return { availability, enabled, estimates, latest, isPending, run };
}
